import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize, map, of, switchMap } from 'rxjs';
import {
  AdditionalDocumentRequestDetailsDto,
  AdditionalDocumentSubmissionDto,
  AdditionalDocumentUploadUrlResponseDto
} from '../../../_core/_model/additional-documents';
import { AdditionalDocumentsService } from '../../../_core/_services/additional-documents.service';
import { NotificationService } from '../../../_core/_services/notification.service';
import { UserNotificationsService } from '../../../_core/_services/user-notifications.service';

@Component({
  selector: 'app-additional-document-details',
  standalone: false,
  templateUrl: './additional-document-details.html',
  styleUrl: './additional-document-details.css'
})
export class AdditionalDocumentDetails implements OnInit {
  requestId = '';
  loading = false;
  uploading = false;
  downloadingId: string | null = null;
  uploadStage = '';
  error: string | null = null;
  request: AdditionalDocumentRequestDetailsDto | null = null;
  selectedFile: File | null = null;
  private pendingPresign: AdditionalDocumentUploadUrlResponseDto | null = null;
  private pendingFileKey = '';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private documents: AdditionalDocumentsService,
    private notify: NotificationService,
    private userNotifications: UserNotificationsService
  ) {}

  ngOnInit(): void {
    this.requestId = this.route.snapshot.paramMap.get('requestId') || '';
    if (!this.requestId) {
      this.router.navigate(['/account/additional-documents']);
      return;
    }
    this.load();
  }

  load(): void {
    this.loading = true;
    this.error = null;

    this.documents.getMineById(this.requestId)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: response => this.request = response.data || null,
        error: error => {
          this.error = error?.error?.message || 'Unable to load this document request.';
          this.notify.showError(this.error!, 'Additional Documents');
        }
      });
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.selectedFile = input.files?.[0] || null;
    const key = this.selectedFile ? this.fileKey(this.selectedFile) : '';
    if (key !== this.pendingFileKey) {
      this.pendingPresign = null;
      this.pendingFileKey = '';
    }
  }

  upload(): void {
    if (!this.request || !this.selectedFile || this.uploading || !this.canUpload) return;

    const file = this.selectedFile;
    this.uploading = true;
    this.uploadStage = 'Preparing secure upload...';
    this.error = null;

    const reusablePresign = this.pendingPresign
      && this.pendingFileKey === this.fileKey(file)
      && new Date(this.pendingPresign.expiresAt).getTime() > Date.now()
      ? this.pendingPresign
      : null;

    const presign$ = reusablePresign
      ? of(reusablePresign)
      : this.documents.createUploadUrl(this.request.id, file).pipe(
          map(response => {
            this.pendingPresign = response.data;
            this.pendingFileKey = this.fileKey(file);
            return response.data;
          })
        );

    presign$.pipe(
      switchMap((presign: AdditionalDocumentUploadUrlResponseDto) => {
        this.uploadStage = 'Uploading document securely...';
        return this.documents.uploadFile(presign.uploadUrl, file).pipe(map(() => presign));
      }),
      switchMap(presign => {
        this.uploadStage = 'Confirming submission...';
        return this.documents.confirmUpload(this.request!.id, presign.submissionId, file);
      }),
      finalize(() => {
        this.uploading = false;
        this.uploadStage = '';
      })
    ).subscribe({
      next: response => {
        this.request = response.data || this.request;
        this.selectedFile = null;
        this.pendingPresign = null;
        this.pendingFileKey = '';
        this.userNotifications.refreshSummary();
        this.notify.showSuccess(
          response.message || 'Document uploaded. Your deposit remains paused while compliance reviews it.',
          'Upload Complete'
        );
      },
      error: error => {
        this.error = error?.error?.error?.details || error?.error?.message || 'Document upload failed. Please try again.';
        this.notify.showError(this.error!, 'Upload Failed');
      }
    });
  }

  download(submission: AdditionalDocumentSubmissionDto): void {
    if (this.downloadingId) return;
    this.downloadingId = submission.id;

    this.documents.getUserDownloadUrl(this.requestId, submission.id)
      .pipe(finalize(() => (this.downloadingId = null)))
      .subscribe({
        next: response => {
          if (response.data?.downloadUrl) window.open(response.data.downloadUrl, '_blank', 'noopener');
        },
        error: error => this.notify.showError(
          error?.error?.message || 'Unable to open the document.',
          'Download Failed'
        )
      });
  }

  private fileKey(file: File): string {
    return `${file.name}:${file.size}:${file.lastModified}:${file.type}`;
  }

  get canUpload(): boolean {
    return !!this.request && ['Requested', 'AwaitingReplacement'].includes(this.request.status);
  }

  statusClass(status: string): string {
    switch (status) {
      case 'Approved': return 'text-bg-success';
      case 'Requested':
      case 'AwaitingReplacement': return 'text-bg-danger';
      case 'Submitted':
      case 'UnderReview': return 'text-bg-warning';
      case 'Rejected': return 'text-bg-danger';
      default: return 'text-bg-secondary';
    }
  }

  fileSize(bytes?: number | null): string {
    if (!bytes) return '—';
    if (bytes < 1024 * 1024) return `${Math.ceil(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
}
